import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import GeminiIcon from './components/GeminiIcon';
import { useTranslation } from 'react-i18next';

/**
 * InteractiveRouteBuilder Component
 * 
 * Replaces Step 6 (Review & Submit) with a conversational, step-by-step itinerary builder.
 * Users progressively validate, modify, or replace destination blocks suggested by Gemini.
 * 
 * Key Features:
 * - Dynamic action set per destination block
 * - Duration adjustment with compensation rules
 * - Change destination with nested selection menu
 * - Real-time chat interface for re-planning
 * - Maintains trip duration invariants
 */

const InteractiveRouteBuilder = ({
  tripData,
  fullSuggestion,
  cityContexts,
  onComplete,
  isLoading,
  onError,
  apiKey,
  logisticSummary = '',
  language = 'en'
}) => {
  const { t } = useTranslation();
  
  // State management
  const [blocks, setBlocks] = useState([]); // Current destination blocks
  const [activePointerIndex, setActivePointerIndex] = useState(0); // Current block being reviewed
  const [confirmedSegments, setConfirmedSegments] = useState([]); // Already approved blocks
  const [chat, setChat] = useState([]); // Conversation history
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDestinationMenu, setShowDestinationMenu] = useState(false);
  const [availableAlternatives, setAvailableAlternatives] = useState([]);
  const [history, setHistory] = useState([]); // For undo/redo
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false); // Summary screen at end
  const [showNewCitiesDialog, setShowNewCitiesDialog] = useState(false); // New cities suggestions dialog
  const [suggestionsList, setSuggestionsList] = useState([]); // List of suggested cities to add
  const [suggestMode, setSuggestMode] = useState('add'); // 'add' | 'replace'
  const [selectedCountryFilter, setSelectedCountryFilter] = useState('all'); // Country filter for alternatives
  const [draggedIndex, setDraggedIndex] = useState(null); // For drag-and-drop reordering
  const [dragOverIndex, setDragOverIndex] = useState(null); // Track drop target
  // (showCityContext removed — city info is always visible)
  const [showChat, setShowChat] = useState(false); // Toggle chat window visibility
  // Semantic cache state — track last suggestion source for feedback + "retry with AI"
  const [lastCacheId, setLastCacheId] = useState(null);
  const [lastSource, setLastSource] = useState(null); // 'cache' | 'stats' | 'ai'
  const [thinkingSteps, setThinkingSteps] = useState([]); // Displayed during loading
  const chatEndRef = useRef(null);

  // Helper to group daily items into city blocks
  const groupIntoBlocks = (items) => {
    if (!items || items.length === 0) return [];
    
    // Check if items truly form multi-day grouped blocks (consecutive same-city items with contiguous day ranges)
    const isReallyGrouped = (items) => {
      if (items.length < 2) return false;
      
      // Check if each item represents a single day and they're consecutive same-city items
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemDuration = item.duration || (item.start_day && item.end_day ? item.end_day - item.start_day + 1 : 1);
        
        // If any item has duration > 1 and it's NOT the only item, it's already grouped
        if (itemDuration > 1 && items.length > 1) {
          return true;
        }
        
        // If it appears to be single-day items, they're not grouped yet
        if (itemDuration === 1 && items.length > 1) {
          continue; // Keep checking
        }
      }
      
      // If we got here and have multiple items, check if they represent a grouped structure
      // (different cities with durations, or marked as pre-grouped)
      if (items.length > 1) {
        const differentCities = new Set(items.map(i => `${i.city},${i.country}`)).size;
        if (differentCities > 1) return true; // Multiple cities = pre-grouped
      }
      
      return false;
    };
    
    if (isReallyGrouped(items)) {
      // Already properly grouped, just ensure duration is calculated
      return items.map(item => {
        const calculatedDuration = item.duration || (item.start_day && item.end_day ? Math.max(1, item.end_day - item.start_day + 1) : 1);
        return {
          ...item,
          duration: Math.max(1, calculatedDuration),
          start_day: item.start_day || 1,
          end_day: item.end_day || (item.start_day || 1)
        };
      });
    }
    
    // Need to group daily items by consecutive same-city days
    const blocks = [];
    let currentBlock = null;
    
    items.forEach((item, idx) => {
      const itemDay = item.day || idx + 1;
      const itemCity = item.city?.trim();
      const itemCountry = item.country?.trim();
      
      if (!currentBlock || 
          currentBlock.city !== itemCity || 
          currentBlock.country !== itemCountry) {
        // Different city or first item - save current block and start new one
        if (currentBlock) blocks.push(currentBlock);
        currentBlock = {
          city: itemCity,
          country: itemCountry,
          start_day: itemDay,
          end_day: itemDay,
          duration: 1,
          explanation: item.explanation || '',
          city_character: item.city_character || '',
          top_highlights: item.top_highlights || [],
          iata: item.iata || ''
        };
      }
    });
    
    if (currentBlock) blocks.push(currentBlock);
    return blocks;
  };

  // Helper to check if a block is a flight-only entry (not a real destination)
  const isFlightBlock = (block) => {
    if (!block || !block.city) return false;
    const city = block.city.toLowerCase();
    // Check for flight patterns like "TLV -> BKK", "Flight", "Departure", etc.
    return city.includes('->') || 
           city.includes('→') ||
           city.includes('flight') ||
           city.includes('depart') ||
           city.includes('arrival') ||
           city.includes('return') ||
           /^[A-Z]{3}\s*\(/.test(block.city); // Matches "BKK (Thailand)" airport format
  };

  // Initialize blocks from fullSuggestion on mount
  useEffect(() => {
    if (fullSuggestion && fullSuggestion.length > 0) {
      // First, ensure data is grouped properly
      const groupedData = groupIntoBlocks(fullSuggestion);
      
      // Filter out flight-only blocks and consolidate with next destination
      let filteredBlocks = [];
      let pendingFlightDays = 0;
      
      groupedData.forEach((item, idx) => {
        if (isFlightBlock(item)) {
          // This is a flight block - add its days to the next destination
          pendingFlightDays += item.duration || 1;
        } else {
          // Real destination - include any pending flight days
          const adjustedStartDay = item.start_day - pendingFlightDays;
          const adjustedDuration = item.duration + pendingFlightDays;
          
          filteredBlocks.push({
            city: item.city,
            country: item.country,
            duration: Math.max(1, adjustedDuration),
            start_day: adjustedStartDay > 0 ? adjustedStartDay : 1,
            end_day: adjustedStartDay + adjustedDuration - 1,
            status: 'Pending',
            order_index: filteredBlocks.length,
            explanation: item.explanation || '',
            iata: item.iata || ''
          });
          
          pendingFlightDays = 0; // Reset after applying
        }
      });
      
      // Recalculate day ranges to be contiguous
      let currentDay = 1;
      filteredBlocks = filteredBlocks.map(block => {
        const updatedBlock = {
          ...block,
          start_day: currentDay,
          end_day: currentDay + block.duration - 1
        };
        currentDay = updatedBlock.end_day + 1;
        return updatedBlock;
      });
      
      console.log('[InteractiveRouteBuilder] Input data:', fullSuggestion);
      console.log('[InteractiveRouteBuilder] Grouped blocks:', groupedData);
      console.log('[InteractiveRouteBuilder] Filtered blocks (no flights):', filteredBlocks);
      setBlocks(filteredBlocks);
      setHistory([filteredBlocks]);
      setHistoryIndex(0);
    }
  }, [fullSuggestion]);

  // Auto-scroll chat to bottom and keep actions visible
  useEffect(() => {
    if (chatEndRef.current) {
      // Scroll the chat container to show the current block and actions
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chat, activePointerIndex, blocks.length, showChat]);

  // Note: Auto-advance removed - now showing summary screen when complete
  // User clicks "Continue to Activities Planning" button to proceed

  // Get current active block
  const getCurrentBlock = () => {
    if (activePointerIndex < blocks.length) {
      return blocks[activePointerIndex];
    }
    return null;
  };

  // Get remaining duration
  const getRemainingDuration = () => {
    const totalDuration = tripData?.duration_days || 0;
    const confirmedDays = confirmedSegments.reduce((sum, seg) => sum + seg.duration, 0);
    return totalDuration - confirmedDays;
  };

  /**
   * Send cache feedback (accept / reject) to the backend.
   * Fire-and-forget — does not block the UI.
   */
  const sendCacheFeedback = (cacheId, action, transitionData = {}) => {
    if (!cacheId) return;
    const token = localStorage.getItem('token');
    fetch(`${API_PATH}/semantic_cache_feedback.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ cache_id: cacheId, action, transition: transitionData }),
    }).catch(() => {}); // Silently ignore network errors for feedback
  };

  /** Source badge shown in chat after a suggestion (cache / stats / ai) */
  const getSourceBadge = (source) => {
    if (source === 'cache')  return ' 🗄️ _(from travel history)_';
    if (source === 'stats')  return ' 📊 _(popular route)_';
    return ''; // 'ai' — no badge, normal Gemini call
  };

  // Helper to recalculate day ranges after reordering or editing
  const recalculateDays = useCallback((blocksToUpdate) => {
    if (!blocksToUpdate || blocksToUpdate.length === 0) return [];
    
    let currentDay = 1;
    const result = [];
    
    for (let idx = 0; idx < blocksToUpdate.length; idx++) {
      const block = blocksToUpdate[idx];
      const duration = block.duration || 1;
      
      result.push({
        ...block,
        start_day: currentDay,
        end_day: currentDay + duration - 1,
        duration: duration,
        order_index: idx
      });
      
      currentDay = currentDay + duration;
    }
    
    return result;
  }, []);

  // Handle going back to edit a confirmed segment
  const handleEditConfirmedSegment = (index) => {
    if (index === 0) {
      addChatMessage('agent', '⚠️ Cannot edit the first destination - it\'s fixed by your arrival airport.');
      return;
    }
    
    // Reset pointer to the selected index
    setActivePointerIndex(index);
    
    // Remove all segments from index onwards from confirmed list
    const newConfirmedSegments = confirmedSegments.slice(0, index);
    setConfirmedSegments(newConfirmedSegments);
    
    // Reset status of blocks from index onwards to 'Pending'
    const updatedBlocks = blocks.map((block, idx) => ({
      ...block,
      status: idx < index ? 'Confirmed' : 'Pending'
    }));
    setBlocks(updatedBlocks);
    
    addChatMessage('agent', `↩️ Going back to Day ${blocks[index].start_day}-${blocks[index].end_day}: ${blocks[index].city}. You can now make changes from here.`);
    saveHistory(updatedBlocks);
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    if (index === 0) return; // Cannot drag first block
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (index === 0 || draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dropIndex === 0 || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    
    // Reorder blocks
    const newBlocks = [...blocks];
    const draggedBlock = newBlocks[draggedIndex];
    
    // Remove from old position
    newBlocks.splice(draggedIndex, 1);
    // Insert at new position
    newBlocks.splice(dropIndex, 0, draggedBlock);
    
    // Recalculate all day ranges
    const updatedBlocks = recalculateDays(newBlocks);
    setBlocks(updatedBlocks);
    
    // Reset confirmed segments if we moved within confirmed area
    if (draggedIndex < activePointerIndex || dropIndex < activePointerIndex) {
      const minIndex = Math.min(draggedIndex, dropIndex);
      setActivePointerIndex(minIndex);
      setConfirmedSegments(confirmedSegments.slice(0, minIndex));
      addChatMessage('agent', `🔄 Reordered itinerary. Please re-confirm from Day ${updatedBlocks[minIndex].start_day}: ${updatedBlocks[minIndex].city}.`);
    } else {
      addChatMessage('agent', `✓ Moved ${draggedBlock.city} to position ${dropIndex + 1}.`);
    }
    
    saveHistory(updatedBlocks);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle block confirmation with Gemini-powered next destination suggestion
  const handleConfirmBlock = async () => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    setIsProcessing(true);

    try {
      // Add to confirmed segments
      const newConfirmedSegments = [...confirmedSegments, currentBlock];
      setConfirmedSegments(newConfirmedSegments);

      // Update block status
      const updatedBlocks = [...blocks];
      updatedBlocks[activePointerIndex].status = 'Confirmed';
      setBlocks(updatedBlocks);

      // Add to chat
      addChatMessage('user', `This works, continue`);
      addChatMessage('agent', `✓ Confirmed ${currentBlock.duration} day${currentBlock.duration !== 1 ? 's' : ''} in ${currentBlock.city}, ${currentBlock.country}.`);

      // Record acceptance feedback for the previously suggested block (if it came from cache)
      if (lastCacheId) {
        const prevBlock = confirmedSegments.length > 0 ? confirmedSegments[confirmedSegments.length - 1] : null;
        sendCacheFeedback(lastCacheId, 'accept', {
          from_city:      prevBlock?.city    || '',
          from_country:   prevBlock?.country || '',
          to_city:        currentBlock.city,
          to_country:     currentBlock.country,
          stay_days:      currentBlock.duration,
          iata:           currentBlock.iata || '',
          to_lat:         currentBlock.lat  || null,
          to_lng:         currentBlock.lng  || null,
          trip_countries: tripData?.destination_countries || [],
        });
        setLastCacheId(null);
      }

      // Calculate remaining days
      const totalDays = tripData?.duration_days || 0;
      const confirmedDays = newConfirmedSegments.reduce((sum, seg) => sum + seg.duration, 0);
      const remainingDays = totalDays - confirmedDays;

      // If all days are planned, remove any blocks after this one and trigger completion
      if (remainingDays <= 0) {
        addChatMessage('agent', '🎉 All days have been planned! Itinerary is complete and ready to finalize.');
        
        // Trim any extra blocks after this one
        const trimmedBlocks = updatedBlocks.slice(0, activePointerIndex + 1);
        setBlocks(trimmedBlocks);
        
        // Move pointer past all blocks to trigger completion
        setActivePointerIndex(trimmedBlocks.length);
        saveHistory(trimmedBlocks);
        setIsProcessing(false);
        return;
      }

      // During preview mode (no trip_id), advance if a next pre-populated block already exists
      if (!tripData.id && activePointerIndex + 1 < updatedBlocks.length) {
        addChatMessage('agent', 'Moving to next destination in your preview itinerary...');
        setActivePointerIndex(activePointerIndex + 1);
        saveHistory(updatedBlocks);
        setIsProcessing(false);
        return;
      }
      // If no more pre-populated blocks remain (or they don't cover all days),
      // fall through to the Gemini suggestion call below

      // Call SmartTravelOrchestrator (semantic cache → stats → Gemini)
      addChatMessage('agent', '⏳ Checking travel history for the best next destination...');
      setThinkingSteps([]);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_PATH}/trips/get_next_destination.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id,
          confirmed_blocks: newConfirmedSegments,
          trip_data: tripData,
          language: language,
          force_ai: false,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get next destination');
      }

      const data = await response.json();

      // Store cache metadata for feedback
      setLastCacheId(data.cache_id || null);
      setLastSource(data.source || 'ai');
      if (data.thinking_steps?.length) {
        setThinkingSteps(data.thinking_steps);
      }

      if (data.destination) {
        // Create new block with Gemini's adaptive suggestion
        const nextBlock = {
          city: data.destination.city,
          country: data.destination.country,
          duration: data.destination.suggested_duration || 3,
          start_day: confirmedDays + 1,
          end_day: confirmedDays + (data.destination.suggested_duration || 3),
          status: 'Pending',
          order_index: activePointerIndex + 1,
          explanation: data.destination.explanation || '',
          city_character: data.destination.city_character || '',
          top_highlights: data.destination.top_highlights || [],
          iata: data.destination.iata || '',
          geographic_notes: data.destination.geographic_notes || ''
        };

        // Update blocks with next suggestion
        const newBlocks = [...updatedBlocks];
        if (activePointerIndex + 1 < newBlocks.length) {
          // Replace the next pre-populated block with Gemini's adaptive suggestion
          newBlocks[activePointerIndex + 1] = nextBlock;
        } else {
          // Add new block if we've exhausted pre-populated suggestions
          newBlocks.push(nextBlock);
        }
        setBlocks(newBlocks);

        // Move to next block
        setActivePointerIndex(activePointerIndex + 1);

        // Show the suggestion with explanations + source badge
        let messageText = `📍 Next suggestion: **${nextBlock.city}, ${nextBlock.country}** (${nextBlock.duration} days)${getSourceBadge(data.source)}\n\n`;
        if (nextBlock.explanation) {
          messageText += `💡 ${nextBlock.explanation}\n`;
        }
        if (nextBlock.city_character) {
          messageText += `🏙️ ${nextBlock.city_character}\n`;
        }
        if (nextBlock.top_highlights && nextBlock.top_highlights.length > 0) {
          messageText += `\n⭐ Must-see:\n${nextBlock.top_highlights.map(h => `• ${h}`).join('\n')}`;
        }
        if (nextBlock.geographic_notes) {
          messageText += `\n✈️ ${nextBlock.geographic_notes}`;
        }
        // Hint that the user can get a fresh AI suggestion if they don't like a cached result
        if (data.source === 'cache' || data.source === 'stats') {
          messageText += '\n\n_Not what you expected? Say **"try AI"** for a fresh suggestion._';
        }
        addChatMessage('agent', messageText);

        saveHistory(newBlocks);
      } else if ((data.remaining_days ?? 0) > 0) {
        // Destination suggestion failed (e.g. country validation) but days remain.
        // Add a placeholder block so the user can continue editing.
        const remainingDaysLeft = data.remaining_days;
        const placeholderBlock = {
          city: 'Next Destination',
          country: '',
          duration: remainingDaysLeft,
          start_day: confirmedDays + 1,
          end_day: confirmedDays + remainingDaysLeft,
          status: 'Pending',
          order_index: activePointerIndex + 1,
          explanation: 'Please choose the next destination',
          iata: ''
        };
        const newBlocks = [...updatedBlocks];
        if (activePointerIndex + 1 < newBlocks.length) {
          newBlocks[activePointerIndex + 1] = placeholderBlock;
        } else {
          newBlocks.push(placeholderBlock);
        }
        setBlocks(newBlocks);
        setActivePointerIndex(activePointerIndex + 1);
        addChatMessage('agent', `⚠️ Could not auto-suggest the next city. ${remainingDaysLeft} days remain — tell me where you'd like to go next, or click "Change Destination" to search!`);
        saveHistory(newBlocks);
      } else {
        addChatMessage('agent', '🎉 All your trip days have been planned! Ready to finalize.');
        setActivePointerIndex(activePointerIndex + 1);
        saveHistory(updatedBlocks);
      }
    } catch (error) {
      addChatMessage('agent', `❌ Error getting next destination: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle duration adjustment
  const handleAdjustDuration = async (adjustment) => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    const newDuration = Math.max(1, currentBlock.duration + adjustment);
    const availableDays = getRemainingDuration() + currentBlock.duration - newDuration;

    if (availableDays < 0) {
      addChatMessage('agent', `❌ Cannot extend stay - only ${availableDays + newDuration} days remaining in the trip.`);
      return;
    }

    // Update block - recalculate end_day based on new duration
    const updatedBlocks = [...blocks];
    updatedBlocks[activePointerIndex].duration = newDuration;
    updatedBlocks[activePointerIndex].end_day = updatedBlocks[activePointerIndex].start_day + newDuration - 1;
    
    // Recalculate start/end days for all downstream blocks
    let currentDay = updatedBlocks[activePointerIndex].end_day + 1;
    for (let i = activePointerIndex + 1; i < updatedBlocks.length; i++) {
      updatedBlocks[i].start_day = currentDay;
      updatedBlocks[i].end_day = currentDay + updatedBlocks[i].duration - 1;
      currentDay = updatedBlocks[i].end_day + 1;
    }
    
    setBlocks(updatedBlocks);

    const action = adjustment > 0 ? 'Stay longer' : 'Stay shorter';
    addChatMessage('user', `${action} in ${currentBlock.city}`);
    addChatMessage('agent', `Adjusted: Now spending ${newDuration} day(s) in ${currentBlock.city}. Re-planning downstream destinations to maintain trip duration.`);

    // Trigger re-planning for downstream blocks
    await triggerReplanning(updatedBlocks);
    saveHistory(updatedBlocks);
  };

  // Handle destination change
  const handleChangeDestination = async () => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    setIsProcessing(true);
    
    try {
      addChatMessage('user', 'Suggest different places');
      addChatMessage('agent', '⏳ Getting fresh alternatives from Gemini...');

      // Send rejection feedback for the current (unwanted) suggestion
      if (lastCacheId) {
        sendCacheFeedback(lastCacheId, 'reject');
        setLastCacheId(null);
      }

      // Build list of ONLY CONFIRMED cities (not unconfirmed blocks)
      const excludedCities = [
        ...confirmedSegments.map(seg => `${seg.city}, ${seg.country}`),
        `${currentBlock.city}, ${currentBlock.country}`  // Also exclude the current block being changed
      ];

      // Country code to name mapping for common codes
      const countryCodeMap = {
        'VN': 'Vietnam',
        'TH': 'Thailand',
        'LA': 'Laos',
        'KH': 'Cambodia',
        'MM': 'Myanmar',
        'MY': 'Malaysia',
        'SG': 'Singapore',
        'ID': 'Indonesia',
        'PH': 'Philippines',
        'JP': 'Japan',
        'KR': 'South Korea',
        'CN': 'China',
        'TW': 'Taiwan',
        'IN': 'India',
        'LK': 'Sri Lanka',
        'NP': 'Nepal'
      };

      // Extract unique countries ONLY from user's selection (not from route!)
      // The route may contain AI-generated countries that user didn't select
      const countriesFromSelection = (tripData.selectedCountriesOrAirports || [])
        .filter(item => item.type === 'country')
        .map(item => {
          const rawValue = item.value || item.label || item.country || item.name || '';
          // Normalize: convert country codes to full names, capitalize first letter
          const normalized = countryCodeMap[rawValue.toUpperCase()] || rawValue;
          // Capitalize first letter of each word
          return normalized.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        })
        .filter(Boolean);
      
      // Use ONLY the user's selected countries, NOT the route countries
      const allCountries = [...new Set(countriesFromSelection)];
      
      console.log('[InteractiveRouteBuilder] handleChangeDestination - selectedCountriesOrAirports:', tripData.selectedCountriesOrAirports);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Countries from selection (normalized):', countriesFromSelection);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Final allowed countries:', allCountries);

      console.log('[InteractiveRouteBuilder] handleChangeDestination - Current block:', currentBlock);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Confirmed segments only:', confirmedSegments);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Excluded cities:', excludedCities);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Trip countries:', allCountries);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Trip ID:', tripData.id);

      // Call Gemini to generate 5 fresh alternatives
      const token = localStorage.getItem('token');
      const requestBody = {
        trip_id: tripData.id || null,  // May be null in preview mode, backend handles it
        excluded_cities: excludedCities,
        duration: currentBlock.duration,
        language: language,
        preferences: tripData.trip_interests || [],
        countries: allCountries,  // Pass all trip countries for multi-country suggestions
        count: 5  // Request 5 alternatives
      };

      // Build trip history from confirmed segments for geographic split logic
      const tripHistoryForAlt = confirmedSegments.map(seg => ({
        city: seg.city,
        country: seg.country
      }));
      const currentTailForAlt = confirmedSegments.length > 0
        ? `${confirmedSegments[confirmedSegments.length - 1].city}, ${confirmedSegments[confirmedSegments.length - 1].country}`
        : '';

      requestBody.trip_history = tripHistoryForAlt;
      requestBody.current_tail = currentTailForAlt;

      console.log('[InteractiveRouteBuilder] handleChangeDestination - Request body:', requestBody);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Trip history for alt:', tripHistoryForAlt);
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Current tail for alt:', currentTailForAlt);

      const response = await fetch(`${API_PATH}/trips/suggest_alternative_destination.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[InteractiveRouteBuilder] handleChangeDestination - Response status:', response.status);

      if (!response.ok) {
        let errorMessage = `Failed to fetch alternatives (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Ignore error parsing errors
        }
        console.error('[InteractiveRouteBuilder] handleChangeDestination - Error:', errorMessage);
        throw new Error(errorMessage);
      }
      
      // Parse response carefully to avoid JSON errors
      let data;
      try {
        const responseText = await response.text();
        console.log('[InteractiveRouteBuilder] handleChangeDestination - Raw response length:', responseText.length);
        
        // Clean up response text - remove any leading/trailing whitespace and non-JSON characters
        const cleanedText = responseText.trim();
        if (!cleanedText.startsWith('{') && !cleanedText.startsWith('[')) {
          throw new Error('Response does not start with JSON');
        }
        
        data = JSON.parse(cleanedText);
      } catch (jsonError) {
        console.error('[InteractiveRouteBuilder] handleChangeDestination - JSON parse error:', jsonError);
        throw new Error(`Server returned invalid JSON: ${jsonError.message}`);
      }
      
      console.log('[InteractiveRouteBuilder] handleChangeDestination - Response data:', data);
      
      // Handle both single destination and array of alternatives
      let alternatives = [];
      if (Array.isArray(data.destinations)) {
        alternatives = data.destinations;
      } else if (data.destination) {
        alternatives = [data.destination];
      } else if (Array.isArray(data)) {
        alternatives = data;
      }

      console.log('[InteractiveRouteBuilder] handleChangeDestination - Parsed alternatives:', alternatives);

      if (alternatives.length > 0) {
        setAvailableAlternatives(alternatives);
        setSelectedCountryFilter('all'); // Reset filter when getting new alternatives
        setShowDestinationMenu(true);
        addChatMessage('agent', `Found ${alternatives.length} alternative destination suggestions. Please select one:`);
      } else {
        addChatMessage('agent', '❌ No alternatives found. Please try again.');
      }
    } catch (error) {
      console.error('[InteractiveRouteBuilder] Error fetching alternatives:', error);
      addChatMessage('agent', `❌ Error fetching alternatives: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle selecting alternative destination
  const handleSelectAlternative = async (alternative) => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    const updatedBlocks = [...blocks];
    updatedBlocks[activePointerIndex] = {
      ...currentBlock,
      city: alternative.city,
      country: alternative.country,
      iata: alternative.iata || '',
      explanation: alternative.explanation || ''
    };
    setBlocks(updatedBlocks);

    addChatMessage('user', `Change to ${alternative.city}, ${alternative.country}`);
    addChatMessage('agent', `Updated: Now visiting ${alternative.city}, ${alternative.country} for ${currentBlock.duration} day(s). Re-planning logistics.`);

    setShowDestinationMenu(false);
    await triggerReplanning(updatedBlocks);
    saveHistory(updatedBlocks);
  };

  // Handle requesting more suggestions for a specific country
  const handleSuggestMore = async (countryFilter = null) => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    setIsProcessing(true);
    
    try {
      addChatMessage('agent', `⏳ Getting more suggestions${countryFilter && countryFilter !== 'all' ? ` from ${countryFilter}` : ''}...`);

      // Build list of cities to exclude (confirmed + current suggestions)
      const excludedCities = [
        ...confirmedSegments.map(seg => `${seg.city}, ${seg.country}`),
        `${currentBlock.city}, ${currentBlock.country}`,
        ...availableAlternatives.map(alt => `${alt.city}, ${alt.country}`)
      ];

      // Country code to name mapping
      const countryCodeMap = {
        'VN': 'Vietnam', 'TH': 'Thailand', 'LA': 'Laos', 'KH': 'Cambodia',
        'MM': 'Myanmar', 'MY': 'Malaysia', 'SG': 'Singapore', 'ID': 'Indonesia',
        'PH': 'Philippines', 'JP': 'Japan', 'KR': 'South Korea', 'CN': 'China'
      };

      // Get countries ONLY from user's selection (not from route!)
      const countriesFromSelection = (tripData.selectedCountriesOrAirports || [])
        .filter(item => item.type === 'country')
        .map(item => {
          const rawValue = item.value || item.label || item.country || item.name || '';
          const normalized = countryCodeMap[rawValue.toUpperCase()] || rawValue;
          return normalized.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        })
        .filter(Boolean);
      let allCountries = [...new Set(countriesFromSelection)];
      
      // If a specific country filter is selected, only request from that country
      if (countryFilter && countryFilter !== 'all') {
        allCountries = [countryFilter];
      }

      const token = localStorage.getItem('token');
      const moreHistoryForAlt = confirmedSegments.map(seg => ({ city: seg.city, country: seg.country }));
      const moreTailForAlt = confirmedSegments.length > 0
        ? `${confirmedSegments[confirmedSegments.length - 1].city}, ${confirmedSegments[confirmedSegments.length - 1].country}`
        : '';
      const requestBody = {
        trip_id: tripData.id || null,
        excluded_cities: excludedCities,
        duration: currentBlock.duration,
        language: language,
        preferences: tripData.trip_interests || [],
        countries: allCountries,
        count: 7,  // Request 7 to buffer against any country-filter losses, show 5
        mode: 'wildcard',  // All pure wildcard suggestions — no 2+3 split logic
        trip_history: moreHistoryForAlt,
        current_tail: moreTailForAlt
      };

      const response = await fetch(`${API_PATH}/trips/suggest_alternative_destination.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch more suggestions (${response.status})`);
      }

      const data = await response.json();
      
      let newAlternatives = [];
      if (Array.isArray(data.destinations)) {
        newAlternatives = data.destinations;
      } else if (data.destination) {
        newAlternatives = [data.destination];
      }

      if (newAlternatives.length > 0) {
        // Append new suggestions to the bottom, avoiding duplicates (case-insensitive)
        const existingKeys = new Set(
          availableAlternatives.map(a => `${a.city}, ${a.country}`.toLowerCase())
        );
        const uniqueNew = newAlternatives.filter(
          alt => !existingKeys.has(`${alt.city}, ${alt.country}`.toLowerCase())
        );
        
        if (uniqueNew.length > 0) {
          setAvailableAlternatives([...availableAlternatives, ...uniqueNew]);
          addChatMessage('agent', `Found ${uniqueNew.length} more suggestion(s)!`);
        } else {
          // AI has exhausted distinct options — route to chat
          setShowDestinationMenu(false);
          setShowChat(true);
          addChatMessage('agent', "✨ I've shown you all the top destinations for this trip! Tell me what you're looking for — a specific vibe, region, or travel style — and I'll find the perfect match for you.");
        }
      } else {
        // No suggestions returned at all — same redirect
        setShowDestinationMenu(false);
        setShowChat(true);
        addChatMessage('agent', "✨ I've shown you all the top destinations for this trip! Tell me what you're looking for — a specific vibe, region, or travel style — and I'll find the perfect match for you.");
      }
    } catch (error) {
      console.error('[InteractiveRouteBuilder] Error fetching more suggestions:', error);
      addChatMessage('agent', `❌ Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle "Something entirely new" option
  const handleSuggestNewDestination = async () => {
    const currentBlock = getCurrentBlock();
    if (!currentBlock) return;

    setIsProcessing(true);
    setShowDestinationMenu(false);

    try {
      const excludedCities = [
        ...confirmedSegments.map(seg => `${seg.city}, ${seg.country}`),
        ...blocks.map(b => `${b.city}, ${b.country}`)
      ];

      // During preview (no trip_id), show local alternative or message
      if (!tripData.id) {
        addChatMessage('agent', `❌ Cannot suggest alternatives during preview. Please confirm your itinerary first to unlock this feature.`);
        setIsProcessing(false);
        return;
      }

      // Call Gemini with negative constraint
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_PATH}/trips/suggest_alternative_destination.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id,
          excluded_cities: excludedCities,
          duration: currentBlock.duration,
          language: language,
          preferences: tripData.trip_interests || []
        })
      });

      if (!response.ok) throw new Error('Failed to suggest alternative');
      
      const data = await response.json();
      if (data.destination) {
        const updatedBlocks = [...blocks];
        updatedBlocks[activePointerIndex] = {
          ...currentBlock,
          city: data.destination.city,
          country: data.destination.country,
          iata: data.destination.iata || '',
          explanation: data.destination.explanation || ''
        };
        setBlocks(updatedBlocks);

        addChatMessage('agent', `✨ New suggestion: ${data.destination.city}, ${data.destination.country}. Does this work for you?`);
        await triggerReplanning(updatedBlocks);
        saveHistory(updatedBlocks);
      }
    } catch (error) {
      addChatMessage('agent', `❌ Error suggesting alternative: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle auto-complete remaining trip with Gemini re-planning
  const handleAutoComplete = async () => {
    setIsProcessing(true);
    
    try {
      addChatMessage('user', 'Plan the rest of the trip for me');
      
      // If no trip_id (preview mode), just confirm blocks as-is
      if (!tripData.id) {
        const updatedBlocks = [...blocks];
        const newConfirmed = [
          ...confirmedSegments,
          ...updatedBlocks.slice(activePointerIndex)
        ];

        setConfirmedSegments(newConfirmed);
        updatedBlocks.forEach((block, idx) => {
          if (idx >= activePointerIndex) {
            updatedBlocks[idx].status = 'Confirmed';
          }
        });
        setBlocks(updatedBlocks);
        addChatMessage('agent', `✓ All remaining destinations confirmed!`);
        setActivePointerIndex(blocks.length);
        saveHistory(updatedBlocks);
        setIsProcessing(false);
        return;
      }

      // Call Gemini to optimize remaining route
      const token = localStorage.getItem('token');
      const currentConfirmed = [
        ...confirmedSegments,
        getCurrentBlock()
      ];

      const response = await fetch(`${API_PATH}/trips/replan_itinerary.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id,
          confirmed_segments: currentConfirmed,
          current_block: blocks[activePointerIndex + 1] || getCurrentBlock(),
          remaining_duration: getRemainingDuration(),
          language: language,
          logistic_summary: logisticSummary || '',
          constraints: {
            preferences: tripData.trip_interests || [],
            excluded_cities: currentConfirmed.map(seg => `${seg.city}, ${seg.country}`),
            allowed_countries: tripData.destination_countries || []
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to optimize route');
      }

      const data = await response.json();
      
      if (data.replanned_blocks && data.replanned_blocks.length > 0) {
        // Update with optimized blocks
        const newBlocks = [...blocks];
        data.replanned_blocks.forEach((repBlock, idx) => {
          const targetIdx = activePointerIndex + 1 + idx;
          if (targetIdx < newBlocks.length) {
            newBlocks[targetIdx] = {
              ...newBlocks[targetIdx],
              city: repBlock.city,
              country: repBlock.country,
              explanation: repBlock.explanation || '',
              city_character: repBlock.city_character || '',
              top_highlights: repBlock.top_highlights || []
            };
          }
        });
        
        setBlocks(newBlocks);
        
        // Now confirm all
        const newConfirmed = [
          ...confirmedSegments,
          ...newBlocks.slice(activePointerIndex)
        ];
        setConfirmedSegments(newConfirmed);
        setActivePointerIndex(newBlocks.length);
        saveHistory(newBlocks);
        
        addChatMessage('agent', `✓ Optimized route planned! All remaining destinations confirmed.`);
      } else {
        // Fallback: just confirm remaining blocks
        const updatedBlocks = [...blocks];
        const newConfirmed = [
          ...confirmedSegments,
          ...updatedBlocks.slice(activePointerIndex)
        ];
        setConfirmedSegments(newConfirmed);
        setActivePointerIndex(updatedBlocks.length);
        saveHistory(updatedBlocks);
        addChatMessage('agent', `✓ All remaining destinations confirmed!`);
      }
    } catch (error) {
      addChatMessage('agent', `❌ Error planning rest of trip: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Trigger re-planning via Gemini
  const triggerReplanning = async (updatedBlocks) => {
    setIsProcessing(true);

    try {
      console.log('[InteractiveRouteBuilder] triggerReplanning called');
      console.log('[InteractiveRouteBuilder] tripData.id:', tripData.id);
      console.log('[InteractiveRouteBuilder] activePointerIndex:', activePointerIndex);
      console.log('[InteractiveRouteBuilder] confirmedSegments:', confirmedSegments);
      
      const token = localStorage.getItem('token');
      
      // Build list of all visited/confirmed cities INCLUDING the current block
      const allVisitedCities = [
        ...confirmedSegments.map(seg => `${seg.city}, ${seg.country}`),
        // Include current block being reviewed (in case it was just changed)
        ...(activePointerIndex < updatedBlocks.length ? 
          [`${updatedBlocks[activePointerIndex].city}, ${updatedBlocks[activePointerIndex].country}`] : 
          [])
      ];
      
      console.log('[InteractiveRouteBuilder] allVisitedCities:', allVisitedCities);
      console.log('[InteractiveRouteBuilder] current_block:', updatedBlocks[activePointerIndex]);

      // Extract allowed countries from user's selection to restrict AI to in-scope countries only
      const replanCountryCodeMap = {
        'VN': 'Vietnam', 'TH': 'Thailand', 'LA': 'Laos', 'KH': 'Cambodia',
        'MM': 'Myanmar', 'MY': 'Malaysia', 'SG': 'Singapore', 'ID': 'Indonesia',
        'PH': 'Philippines', 'JP': 'Japan', 'KR': 'South Korea', 'CN': 'China',
        'TW': 'Taiwan', 'IN': 'India', 'LK': 'Sri Lanka', 'NP': 'Nepal',
        'HK': 'Hong Kong', 'MO': 'Macau', 'MN': 'Mongolia', 'BT': 'Bhutan',
        'BD': 'Bangladesh', 'PK': 'Pakistan', 'AF': 'Afghanistan', 'IR': 'Iran',
        'IQ': 'Iraq', 'TR': 'Turkey', 'GE': 'Georgia', 'AZ': 'Azerbaijan',
        'AM': 'Armenia', 'UZ': 'Uzbekistan', 'KZ': 'Kazakhstan', 'KG': 'Kyrgyzstan',
        'TJ': 'Tajikistan', 'TM': 'Turkmenistan'
      };
      const replanAllowedCountries = (tripData.selectedCountriesOrAirports || [])
        .filter(item => item.type === 'country')
        .map(item => {
          const rawValue = item.value || item.label || '';
          const normalized = replanCountryCodeMap[rawValue.toUpperCase()] || rawValue;
          return normalized.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        })
        .filter(Boolean);
      const allowedCountriesForReplan = replanAllowedCountries.length > 0
        ? replanAllowedCountries
        : (tripData.destination_countries || []);

      const response = await fetch(`${API_PATH}/trips/replan_itinerary.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id || null,  // May be null in preview mode
          confirmed_segments: confirmedSegments,
          current_block: updatedBlocks[activePointerIndex],
          remaining_duration: getRemainingDuration(),
          language: language,
          logistic_summary: logisticSummary || '',
          constraints: {
            preferences: tripData.trip_interests || [],
            excluded_cities: allVisitedCities,
            allowed_countries: allowedCountriesForReplan
          }
        })
      });

      console.log('[InteractiveRouteBuilder] API response status:', response.status);
      
      if (!response.ok) throw new Error('Re-planning failed');

      const data = await response.json();
      console.log('[InteractiveRouteBuilder] API response data:', data);
      
      if (data.replanned_blocks && data.replanned_blocks.length > 0) {
        // Update downstream blocks with re-planned suggestions
        const newBlocks = [...updatedBlocks];
        
        // Calculate starting day for replanned blocks (day after current block ends)
        let currentDay = newBlocks[activePointerIndex].end_day + 1;
        
        data.replanned_blocks.forEach((repBlock, idx) => {
          const targetIdx = activePointerIndex + 1 + idx;
          if (targetIdx < newBlocks.length) {
            // Update with new city/country info but preserve duration if not provided
            const duration = repBlock.duration || newBlocks[targetIdx].duration || 1;
            newBlocks[targetIdx] = {
              ...newBlocks[targetIdx],
              ...repBlock,
              duration: duration,
              start_day: currentDay,
              end_day: currentDay + duration - 1
            };
            currentDay = newBlocks[targetIdx].end_day + 1;
          }
        });
        
        setBlocks(newBlocks);
        console.log('[InteractiveRouteBuilder] Blocks updated with replanned destinations:', newBlocks);
        addChatMessage('agent', `✓ Re-planned remaining destinations!`);
      } else {
        console.log('[InteractiveRouteBuilder] No replanned blocks returned');
      }
    } catch (error) {
      console.error('[InteractiveRouteBuilder] Replanning error:', error);
      addChatMessage('agent', `⚠️ Replanning encountered an issue: ${error.message}. Please review manually.`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle generic chat input
  // directMessage — when provided, bypass the text field and process this message directly
  const handleSendMessage = async (directMessage = null) => {
    const message = directMessage !== null ? directMessage : userInput;
    if (!message.trim()) return;

    addChatMessage('user', message);
    const userQuery = message;
    if (directMessage === null) setUserInput('');
    setIsProcessing(true);

    try {
      // During preview (no trip_id), process locally without calling backend
      if (!tripData.id) {
        // Parse common adjustments locally
        const lowerQuery = userQuery.toLowerCase();
        let changesApplied = false;
        let updatedBlocks = [...blocks];
        const messages = [];
        
        // Current block being reviewed - only allow changes to this and future blocks
        const currentBlockIdx = activePointerIndex;
        const currentBlock = blocks[currentBlockIdx];
        
        // Split by "then", "and then", ",", ";" to handle multiple instructions
        const instructions = userQuery.split(/\s+then\s+|\s*,\s*|\s*;\s*|\s+and\s+then\s+/i).filter(s => s.trim());
        
        for (const instruction of instructions) {
          // Check for day/duration adjustments like "day 18-22 da nang" or "18-22 da nang"
          const dayRangeMatch = instruction.match(/(?:day\s*)?(\d+)\s*[-–to]+\s*(\d+)\s+(.+)/i);
          if (dayRangeMatch) {
            const startDay = parseInt(dayRangeMatch[1]);
            const endDay = parseInt(dayRangeMatch[2]);
            let cityName = dayRangeMatch[3].trim();
            // Clean up city name - remove trailing words like "days", "rest", etc.
            cityName = cityName.replace(/\s+(days?|rest|back|return).*$/i, '').trim();
            const duration = endDay - startDay + 1;
            
            // Find or create block for this city
            const existingIdx = updatedBlocks.findIndex(b => 
              b.city.toLowerCase().includes(cityName.toLowerCase()) || 
              cityName.toLowerCase().includes(b.city.toLowerCase())
            );
            
            if (existingIdx >= 0) {
              // Only allow modifying current block or later - confirmed blocks are locked
              if (existingIdx < currentBlockIdx) {
                messages.push(`⚠️ ${updatedBlocks[existingIdx].city} is already confirmed. Click on it above to edit.`);
                continue;
              }
              updatedBlocks[existingIdx] = {
                ...updatedBlocks[existingIdx],
                start_day: startDay,
                end_day: endDay,
                duration: duration
              };
              messages.push(`✓ ${updatedBlocks[existingIdx].city}: days ${startDay}-${endDay} (${duration}d)`);
              changesApplied = true;
            } else {
              // Try to infer country from context or existing blocks
              let inferredCountry = blocks[0]?.country || 'Unknown';
              // Special handling for known cities
              const cityLower = cityName.toLowerCase();
              if (['chiang mai', 'chang mai', 'chiangmai', 'pattaya', 'phuket', 'krabi', 'bangkok'].includes(cityLower)) {
                inferredCountry = 'Thailand';
              } else if (['da nang', 'danang', 'hanoi', 'ho chi minh', 'hoi an', 'nha trang', 'sapa'].includes(cityLower)) {
                inferredCountry = 'Vietnam';
              }
              
              // Normalize city name
              const normalizedCity = cityName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
              
              const newBlock = {
                city: normalizedCity,
                country: inferredCountry,
                start_day: startDay,
                end_day: endDay,
                duration: duration,
                status: 'Pending',
                order_index: updatedBlocks.length,
                explanation: ''
              };
              updatedBlocks.push(newBlock);
              messages.push(`✓ Added ${normalizedCity} (${inferredCountry}): days ${startDay}-${endDay}`);
              changesApplied = true;
            }
            continue;
          }
          
          // Check for "X days [city]" or "[city] for X days" patterns
          const daysPattern = instruction.match(/(\d+)\s*days?\s+(.+)/i) || 
                              instruction.match(/(.+?)\s+(?:for|with)\s*(\d+)\s*days?/i);
          if (daysPattern) {
            let days, cityName;
            if (/^\d+/.test(daysPattern[1])) {
              days = parseInt(daysPattern[1]);
              cityName = daysPattern[2].trim();
            } else {
              cityName = daysPattern[1].trim();
              days = parseInt(daysPattern[2]);
            }
            
            const existingIdx = updatedBlocks.findIndex(b => 
              b.city.toLowerCase().includes(cityName.toLowerCase()) || 
              cityName.toLowerCase().includes(b.city.toLowerCase())
            );
            
            if (existingIdx >= 0) {
              // Only allow modifying current block or later - confirmed blocks are locked
              if (existingIdx < currentBlockIdx) {
                messages.push(`⚠️ ${updatedBlocks[existingIdx].city} is already confirmed. Click on it above to edit.`);
                continue;
              }
              updatedBlocks[existingIdx].duration = days;
              messages.push(`✓ ${updatedBlocks[existingIdx].city}: ${days} days`);
              changesApplied = true;
            }
            continue;
          }
          
          // Check for "replace [city] with [city]" or "change to [city]" patterns
          const replacePattern = instruction.match(/(?:replace\s+(?:\w+\s+)?with|change\s+(?:this\s+)?to|switch\s+to|make\s+(?:it|this)\s+)\s*(.+)/i);
          if (replacePattern) {
            const newCityName = replacePattern[1].trim().replace(/\s+(instead|please).*$/i, '');
            // Replace current block with the new city
            const normalizedCity = newCityName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            
            // Infer country
            let inferredCountry = blocks[currentBlockIdx]?.country || 'Unknown';
            const cityLower = newCityName.toLowerCase();
            if (['chiang mai', 'chang mai', 'chiangmai', 'pattaya', 'phuket', 'krabi', 'bangkok', 'bkk'].includes(cityLower)) {
              inferredCountry = 'Thailand';
            } else if (['da nang', 'danang', 'hanoi', 'ho chi minh', 'hoi an', 'nha trang', 'sapa'].includes(cityLower)) {
              inferredCountry = 'Vietnam';
            }
            
            updatedBlocks[currentBlockIdx] = {
              ...updatedBlocks[currentBlockIdx],
              city: normalizedCity === 'Bkk' ? 'Bangkok' : normalizedCity,
              country: inferredCountry
            };
            messages.push(`✓ Replaced ${blocks[currentBlockIdx].city} with ${normalizedCity === 'Bkk' ? 'Bangkok' : normalizedCity}`);
            changesApplied = true;
            continue;
          }
          
          // Check for "last X days in [city]" or "end in [city]" patterns
          const lastDaysPattern = instruction.match(/(?:last|final|end(?:ing)?)\s*(\d+)?\s*days?\s+(?:in|at|back\s+(?:to|in)?)\s*(.+)/i);
          if (lastDaysPattern) {
            const days = parseInt(lastDaysPattern[1]) || 2;
            let cityName = lastDaysPattern[2].trim();
            const normalizedCity = cityName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            
            // Infer country
            let inferredCountry = blocks[0]?.country || 'Unknown';
            const cityLower = cityName.toLowerCase();
            if (['chiang mai', 'chang mai', 'chiangmai', 'pattaya', 'phuket', 'krabi', 'bangkok', 'bkk'].includes(cityLower)) {
              inferredCountry = 'Thailand';
            } else if (['da nang', 'danang', 'hanoi', 'ho chi minh', 'hoi an', 'nha trang', 'sapa'].includes(cityLower)) {
              inferredCountry = 'Vietnam';
            }
            
            // Add or update block at the end for the return city
            const lastBlockIdx = updatedBlocks.length - 1;
            if (lastBlockIdx >= currentBlockIdx) {
              // Replace the last block with return to this city
              updatedBlocks[lastBlockIdx] = {
                ...updatedBlocks[lastBlockIdx],
                city: normalizedCity === 'Bkk' ? 'Bangkok' : normalizedCity,
                country: inferredCountry,
                duration: days
              };
              messages.push(`✓ Last ${days} days: returning to ${normalizedCity === 'Bkk' ? 'Bangkok' : normalizedCity}`);
              changesApplied = true;
            }
            continue;
          }
          
          // Check for "the rest [city]" or "rest in [city]" pattern  
          const restPattern = instruction.match(/(?:the\s+)?rest\s+(?:in\s+|back\s+(?:in\s+|to\s+)?)?(.+)/i);
          if (restPattern) {
            const cityName = restPattern[1].trim();
            const existingIdx = updatedBlocks.findIndex(b => 
              b.city.toLowerCase().includes(cityName.toLowerCase()) || 
              cityName.toLowerCase().includes(b.city.toLowerCase())
            );
            
            if (existingIdx >= 0) {
              // Only allow modifying current block or later - confirmed blocks are locked
              if (existingIdx < currentBlockIdx) {
                messages.push(`⚠️ ${updatedBlocks[existingIdx].city} is already confirmed. Click on it above to edit.`);
                continue;
              }
              // Calculate remaining days
              const totalDays = tripData?.duration_days || 30;
              const usedDays = updatedBlocks.reduce((sum, b, i) => i !== existingIdx ? sum + b.duration : sum, 0);
              const remainingDays = Math.max(1, totalDays - usedDays);
              updatedBlocks[existingIdx].duration = remainingDays;
              messages.push(`✓ ${updatedBlocks[existingIdx].city}: ${remainingDays} days (remaining)`);
              changesApplied = true;
            }
          }
        }
        
        if (changesApplied) {
          // Sort by start_day and recalculate
          updatedBlocks = updatedBlocks.sort((a, b) => a.start_day - b.start_day);
          setBlocks(recalculateDays(updatedBlocks));
          saveHistory(updatedBlocks);
          addChatMessage('agent', messages.join('\n'));
          
          // Don't reset - confirmed segments remain confirmed, continue from current position
          addChatMessage('agent', '✓ Changes applied to the current block. Confirmed cities remain unchanged.');
          setIsProcessing(false);
          return;
        }
        
        // --- Smart fallback: detect context and show interactive options ---

        // 1. Detect city suggestion requests (e.g. "cities with water attractions")
        const previewSuggestionKeywords = ['suggest', 'recommend', 'find cities', 'show cities', 'list cities',
          'places with', 'cities with', 'beach', 'water attractions', 'mountain', 'temples',
          'food scene', 'nightlife', 'shopping', 'what about', 'known for', 'famous for', 'show me'];
        const isPreviewSuggestionRequest = previewSuggestionKeywords.some(k => lowerQuery.includes(k));
        if (isPreviewSuggestionRequest) {
          await handleSuggestNewCitiesToAdd(userQuery);
          return;
        }

        // 2. Detect a bare city name (1-4 words, no digits, no command keywords)
        const commandLeadWords = ['day', 'change', 'replace', 'switch', 'add', 'set', 'last',
          'final', 'end', 'rest', 'from', 'the', 'help', 'how', 'what', 'which', 'can', 'i',
          'want', 'need', 'please', 'tell', 'show', 'find', 'give', 'suggest', 'list'];
        const queryWords = userQuery.trim().split(/\s+/);
        const looksLikeCity = queryWords.length >= 1 && queryWords.length <= 4
          && !/\d/.test(userQuery)
          && !commandLeadWords.includes(queryWords[0].toLowerCase());
        if (looksLikeCity) {
          const normalizedCity = queryWords.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
          const s = currentBlock?.start_day || 'X';
          const e = currentBlock?.end_day || 'Y';
          const dur = currentBlock?.duration || 3;
          addChatMessage('agent',
            `🏙️ Looks like you mentioned "${normalizedCity}". What would you like to do?`,
            [
              { label: `✏️ Replace current block with ${normalizedCity}`, fill: `change to ${normalizedCity}`, autoSubmit: true },
              { label: `📅 Assign days ${s}–${e} to ${normalizedCity}`, fill: `day ${s}-${e} ${normalizedCity}`, autoSubmit: true },
              { label: `🔚 Set as last destination (${dur} days)`, fill: `last ${dur} days in ${normalizedCity}`, autoSubmit: true },
            ]
          );
          setIsProcessing(false);
          return;
        }

        // 3. Generic interactive help (replaces the old static text)
        const s = currentBlock?.start_day || 'X';
        const e = currentBlock?.end_day || 'Y';
        addChatMessage('agent',
          `💡 I didn't understand that. What would you like to do?`,
          [
            { label: '✏️ Change current city', fill: 'change to ' },
            { label: '📅 Set specific days', fill: `day ${s}-${e} ` },
            { label: '⏱️ Adjust stay duration', fill: `3 days ${currentBlock?.city || ''}`.trim() },
            { label: '➕ Add a city to the trip', fill: `day ${s}-${e} ` },
            { label: '🔚 Set last N days in a city', fill: 'last 3 days in ' },
            { label: '🔍 Find cities with a theme', fill: 'cities with ' },
          ]
        );
        setIsProcessing(false);
        return;
      }

      // ── "Try AI" — bypass cache, get a fresh Gemini suggestion ──────────────
      const tryAiKeywords = ['try ai', 'try gemini', 'fresh suggestion', 'ask ai', 'use ai', 'force ai', 'new suggestion'];
      const isTryAi = tryAiKeywords.some(kw => userQuery.toLowerCase().includes(kw));

      if (isTryAi && lastCacheId) {
        // Send rejection feedback for the cached suggestion
        sendCacheFeedback(lastCacheId, 'reject');
        setLastCacheId(null);
        addChatMessage('agent', '🤖 Getting a fresh suggestion from AI...');

        const confirmedDays = confirmedSegments.reduce((sum, seg) => sum + seg.duration, 0);
        const tkn = localStorage.getItem('token');
        const freshResp = await fetch(`${API_PATH}/trips/get_next_destination.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tkn}` },
          body: JSON.stringify({
            trip_id: tripData.id,
            confirmed_blocks: confirmedSegments,
            trip_data: tripData,
            language: language,
            force_ai: true,
          }),
        });
        if (freshResp.ok) {
          const freshData = await freshResp.json();
          setLastCacheId(freshData.cache_id || null);
          setLastSource('ai');
          if (freshData.destination) {
            const nb = {
              city: freshData.destination.city,
              country: freshData.destination.country,
              duration: freshData.destination.suggested_duration || 3,
              start_day: confirmedDays + 1,
              end_day: confirmedDays + (freshData.destination.suggested_duration || 3),
              status: 'Pending',
              order_index: activePointerIndex + 1,
              explanation: freshData.destination.explanation || '',
              city_character: freshData.destination.city_character || '',
              top_highlights: freshData.destination.top_highlights || [],
              iata: freshData.destination.iata || '',
              geographic_notes: freshData.destination.geographic_notes || '',
            };
            const newBlocks = [...blocks];
            if (activePointerIndex + 1 < newBlocks.length) newBlocks[activePointerIndex + 1] = nb;
            else newBlocks.push(nb);
            setBlocks(newBlocks);
            let msg = `🤖 AI suggests: **${nb.city}, ${nb.country}** (${nb.duration} days)\n\n`;
            if (nb.explanation) msg += `💡 ${nb.explanation}\n`;
            addChatMessage('agent', msg);
            saveHistory(newBlocks);
          }
        }
        setIsProcessing(false);
        return;
      }

      // Check if user is asking for new city suggestions
      const suggestKeywords = ['suggest', 'new city', 'other city', 'different city', 'alternative', 'add city', 'more city', 'other place', 'different place'];
      const isSuggestingNewCities = suggestKeywords.some(keyword => userQuery.toLowerCase().includes(keyword));
      
      if (isSuggestingNewCities) {
        // Use specialized new cities suggestion handler
        await handleSuggestNewCitiesToAdd(userQuery);
        return;
      }

      // Send to agent for generic instruction processing
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_PATH}/trips/process_revision_instruction.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id,
          instruction: userQuery,
          current_blocks: blocks,
          confirmed_segments: confirmedSegments,
          active_pointer: activePointerIndex,
          language: language
        })
      });

      if (!response.ok) throw new Error('Failed to process instruction');

      const data = await response.json();
      addChatMessage('agent', data.response || 'Processing your request...');

      if (data.updated_blocks && data.updated_blocks.length > 0) {
        // Backend returns ONLY the modified current block
        // Merge: keep confirmed blocks + update current block + keep remaining blocks
        const updatedBlocks = [...blocks];
        
        // Replace current block with the updated one from backend
        if (data.updated_blocks[0]) {
          updatedBlocks[activePointerIndex] = {
            ...updatedBlocks[activePointerIndex],
            ...data.updated_blocks[0]
          };
        }
        
        // Recalculate days
        const blocksWithDays = recalculateDays(updatedBlocks);
        setBlocks(blocksWithDays);
        saveHistory(blocksWithDays);
        
        // Don't reset - only current block was modified, confirmed segments stay
        addChatMessage('agent', '✓ Changes applied to current city.');
      }
    } catch (error) {
      addChatMessage('agent', `❌ Error: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle request for new cities to add to the itinerary
  const handleSuggestNewCitiesToAdd = async (userQuery) => {
    try {
      addChatMessage('agent', '⏳ Getting suggestions for new cities to add to your trip...');

      // Exclude ALL cities already in the plan (confirmed + pending blocks, deduped)
      const currentCities = [...new Set([
        ...confirmedSegments.map(s => `${s.city}, ${s.country}`),
        ...blocks.map(b => `${b.city}, ${b.country}`)
      ])];

      // Resolve allowed countries (same logic as handleChangeDestination)
      const ncCountryCodeMap = {
        'VN': 'Vietnam', 'TH': 'Thailand', 'LA': 'Laos', 'KH': 'Cambodia',
        'MM': 'Myanmar', 'MY': 'Malaysia', 'SG': 'Singapore', 'ID': 'Indonesia',
        'PH': 'Philippines', 'JP': 'Japan', 'KR': 'South Korea', 'CN': 'China',
        'TW': 'Taiwan', 'IN': 'India', 'LK': 'Sri Lanka', 'NP': 'Nepal'
      };
      const ncAllowedCountries = [...new Set(
        (tripData.selectedCountriesOrAirports || [])
          .filter(item => item.type === 'country')
          .map(item => {
            const raw = item.value || item.label || item.country || item.name || '';
            const norm = ncCountryCodeMap[raw.toUpperCase()] || raw;
            return norm.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
          })
          .filter(Boolean)
      )];

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_PATH}/trips/suggest_new_cities.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripData.id || null,  // May be null in preview mode
          current_cities: currentCities,
          language: language,
          preferences: tripData.trip_interests || [],
          count: 5,
          countries: ncAllowedCountries
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch new city suggestions (${response.status})`);
      }

      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error('[InteractiveRouteBuilder] JSON parse error:', jsonError);
        throw new Error(`Server returned invalid JSON: ${jsonError.message}`);
      }
      
      if (data.suggestions && data.suggestions.length > 0) {
        // Filter out airport names
        const isAirportName = (cityName) => {
          const airports = ['suvarnabhumi', 'changi', 'incheon', 'narita', 'haneda', 'cdg', 'orly', 'heathrow', 'gatwick', 'jfk', 'lax', 'ohare', 'hartsfield', 'airport'];
          return airports.some(airport => cityName.toLowerCase().includes(airport));
        };
        
        const validSuggestions = data.suggestions.filter(s => !isAirportName(s.city));
        
        if (validSuggestions.length > 0) {
          // Format suggestions as a nice list
          let suggestionText = '✨ Here are some great cities to consider adding to your trip:\n\n';
          validSuggestions.forEach((city, idx) => {
            suggestionText += `${idx + 1}. **${city.city}, ${city.country}** - ${city.explanation}\n`;
          });
          
          addChatMessage('agent', suggestionText);

          // Store suggestions for potential selection
          setSuggestionsList(validSuggestions);
          setShowNewCitiesDialog(true);
        } else {
          addChatMessage('agent', '❌ The suggestions included airport names instead of cities. Please try again.');
        }
      } else {
        addChatMessage('agent', '❌ Could not generate new city suggestions. Please try again.');
      }
    } catch (error) {
      console.error('[InteractiveRouteBuilder] Error getting suggestions:', error);
      addChatMessage('agent', `❌ Error getting suggestions: ${error.message}`);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Chat helper
  const addChatMessage = (sender, text, options = []) => {
    setChat(prev => [...prev, { sender, text, options, timestamp: new Date() }]);
  };

  // Undo/Redo helpers
  const saveHistory = (newBlocks) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newBlocks);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setBlocks(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setBlocks(history[historyIndex + 1]);
    }
  };

  // Calculate total confirmed days
  const getTotalConfirmedDays = () => {
    return confirmedSegments.reduce((sum, seg) => sum + seg.duration, 0);
  };

  // Check if review is complete - either all blocks confirmed OR all trip days filled
  const totalTripDays = tripData?.duration_days || 0;
  const totalConfirmedDays = getTotalConfirmedDays();
  const isComplete = activePointerIndex >= blocks.length || (totalConfirmedDays >= totalTripDays && totalTripDays > 0);

  // Get current block actions
  const currentBlock = getCurrentBlock();
  const showStayLonger = !currentBlock || currentBlock.duration < (tripData?.duration_days || 0);
  const showStayShorter = currentBlock && currentBlock.duration > 1;

  // Get unique visited cities
  const getVisitedCities = () => {
    const visited = new Set();
    confirmedSegments.forEach(seg => {
      visited.add(`${seg.city}, ${seg.country}`);
    });
    blocks.slice(0, activePointerIndex).forEach(block => {
      visited.add(`${block.city}, ${block.country}`);
    });
    return visited;
  };

  // Get unique alternatives (remove duplicates)
  const getUniqueAlternatives = () => {
    const seen = new Set();
    const unique = [];
    availableAlternatives.forEach(alt => {
      const key = `${alt.city}, ${alt.country}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(alt);
      }
    });
    return unique;
  };

  return (
    <>
      {/* Auto-advance: Confirmation dialog removed - automatically jumps to Step 7 */}

      {/* SUMMARY SCREEN - When itinerary is complete */}
      {isComplete && blocks.length > 0 && (
        <div className="tw-p-6 tw-overflow-y-auto tw-flex-1" style={{ minHeight: 0 }}>
          {/* Header */}
          <div className="tw-text-center tw-mb-5">
            <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-green-500/20 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <span className="tw-text-2xl">🎉</span>
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-muted-fore tw-mb-1">Your Itinerary is Ready!</h2>
            <p className="tw-text-sm tw-text-muted">{totalTripDays} days across {blocks.length} destination{blocks.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="tw-border-t tw-border-surface-border tw-mb-5" />

          {/* Trip Summary */}
          <div className="tw-mb-5">
            <h3 className="tw-text-sm tw-font-semibold tw-text-muted-fore tw-mb-3">📋 Trip Summary</h3>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {blocks.map((block, idx) => (
                <div
                  key={idx}
                  className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-xl tw-border tw-border-surface-border tw-bg-surface-3/40"
                >
                  <div className="tw-flex-shrink-0 tw-w-12 tw-text-center tw-pr-3 tw-border-r tw-border-accent/30">
                    <span className="tw-text-xs tw-text-muted tw-block">Day</span>
                    <span className="tw-text-sm tw-font-bold tw-text-accent-glow">
                      {block.start_day}{block.start_day !== block.end_day ? `–${block.end_day}` : ''}
                    </span>
                  </div>
                  <div className="tw-flex-1">
                    <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{block.city}</p>
                    <span className="tw-text-xs tw-text-muted">{block.country} · {block.duration} day{block.duration !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="tw-flex-shrink-0 tw-w-6 tw-h-6 tw-rounded-full tw-bg-green-500/20 tw-flex tw-items-center tw-justify-center">
                    <span className="tw-text-xs tw-text-green-400 tw-font-bold">✓</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tw-border-t tw-border-surface-border tw-mb-5" />

          {/* Action Buttons */}
          <div className="tw-text-center">
            <p className="tw-text-sm tw-text-muted tw-mb-4">Ready to continue? We'll now build your detailed daily activities.</p>
            <div className="tw-flex tw-justify-center tw-gap-3">
              <button
                type="button"
                onClick={() => {
                  setActivePointerIndex(blocks.length - 1);
                  const updatedBlocks = [...blocks];
                  updatedBlocks[blocks.length - 1].status = 'Pending';
                  setBlocks(updatedBlocks);
                  setConfirmedSegments(confirmedSegments.slice(0, -1));
                }}
                className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-border tw-border-surface-border tw-text-sm tw-text-muted tw-transition-all tw-duration-200 tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3"
              >
                ← Back to Edit
              </button>
              <button
                type="button"
                onClick={() => onComplete?.(blocks)}
                className="tw-px-6 tw-py-2.5 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110"
              >
                Continue to Activities Planning →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHAT INTERFACE - Main interaction area (only when NOT complete) */}
      {!isComplete && (
      <div className="tw-flex tw-flex-col tw-overflow-hidden tw-flex-1">

      {/* Current Block Display */}
      {currentBlock && (
        <div className="tw-p-4 tw-overflow-y-auto tw-flex-1">
          <div className="tw-rounded-xl tw-border-2 tw-border-accent/40 tw-bg-surface-3/40 tw-p-4">
            {/* Block Header */}
            <div className="tw-mb-3">
              <span className="tw-text-xs tw-font-semibold tw-text-accent-glow tw-uppercase tw-tracking-wider">📍 Next Destination</span>
              <h3 className="tw-text-lg tw-font-bold tw-text-muted-fore tw-mt-1 tw-leading-tight">
                {currentBlock.start_day && currentBlock.end_day ? (
                  <>Day {currentBlock.start_day}{currentBlock.start_day !== currentBlock.end_day ? `–${currentBlock.end_day}` : ''}: </>
                ) : ''}
                {currentBlock.city}, {currentBlock.country}
              </h3>
              <p className="tw-text-xs tw-text-muted tw-mt-0.5">
                ⏱️ {currentBlock.duration} day{currentBlock.duration !== 1 ? 's' : ''}
                <span className="tw-text-muted/60 tw-ml-1">({totalConfirmedDays + currentBlock.duration} of {totalTripDays} total)</span>
              </p>
            </div>

            {/* First Block Warning */}
            {activePointerIndex === 0 && (
              <div className="tw-flex tw-items-start tw-gap-2 tw-p-2.5 tw-rounded-xl tw-bg-accent/10 tw-border tw-border-accent/20 tw-mb-3">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="tw-text-accent-glow tw-flex-shrink-0 tw-mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                <p className="tw-text-xs tw-text-muted-fore">This is your arrival destination (fixed by your selected airport).</p>
              </div>
            )}

            {/* City Info Card */}
            {(() => {
              const ctx = cityContexts?.[`${currentBlock.city}, ${currentBlock.country}`];
              const cityCharacter  = ctx?.city_nature     || currentBlock.city_character || '';
              const whySelected    = ctx?.why_selected    || currentBlock.explanation    || '';
              const highlights     = currentBlock.top_highlights || [];
              if (!cityCharacter && !whySelected && highlights.length === 0) return null;
              return (
                <div className="tw-p-3 tw-rounded-xl tw-bg-accent/5 tw-border tw-border-accent/20 tw-mb-3 tw-space-y-2">
                  {cityCharacter && (
                    <div>
                      <span className="tw-text-xs tw-font-semibold tw-text-accent-glow tw-block tw-mb-0.5">🏙️ City character</span>
                      <span className="tw-text-xs tw-text-muted">{cityCharacter}</span>
                    </div>
                  )}
                  {whySelected && (
                    <div>
                      <span className="tw-text-xs tw-font-semibold tw-text-accent-glow tw-block tw-mb-0.5">💡 Why it was selected</span>
                      <span className="tw-text-xs tw-text-muted">{whySelected.split(/\s+/).slice(0, 15).join(' ')}{whySelected.split(/\s+/).length > 15 ? '…' : ''}</span>
                    </div>
                  )}
                  {highlights.length > 0 && (
                    <div>
                      <span className="tw-text-xs tw-font-semibold tw-text-accent-glow tw-block tw-mb-0.5">⭐ Major attractions</span>
                      {highlights.map((h, i) => {
                        const name = h.replace(/\s*[-–:,].*$/, '').trim();
                        return <span key={i} className="tw-text-xs tw-text-muted tw-block tw-pl-2">• {name}</span>;
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Compact Action Buttons */}
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-mt-3">
              {/* Confirm Button */}
              <Tooltip title="Confirm and move to next city">
                <button
                  type="button"
                  onClick={handleConfirmBlock}
                  disabled={isProcessing}
                  aria-label="Confirm and move to next city"
                  className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-bg-green-600 tw-text-white tw-text-xs tw-font-semibold tw-transition-all tw-duration-150 tw-hover:tw-brightness-110 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                >
                  <CheckIcon fontSize="small" style={{ width: 14, height: 14 }} />
                  Confirm
                </button>
              </Tooltip>
              
              {/* Duration Dropdown */}
              {(showStayLonger || showStayShorter) && (
                <div className="tw-flex tw-items-center tw-gap-1">
                  <CalendarTodayIcon sx={{ fontSize: 16, color: '#8b949e' }} />
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={currentBlock?.duration || 1}
                      onChange={(e) => {
                        const newDuration = e.target.value;
                        const currentDuration = currentBlock?.duration || 1;
                        const adjustment = newDuration - currentDuration;
                        if (adjustment !== 0) {
                          handleAdjustDuration(adjustment);
                        }
                      }}
                      disabled={isProcessing}
                      sx={{ 
                        height: 32,
                        fontSize: '0.875rem',
                        bgcolor: 'rgb(128,128,255)',
                        color: '#e6edf3',
                        '& .MuiSelect-select': { py: 0.5, px: 1, color: '#e6edf3' },
                        '& .MuiSvgIcon-root': { color: '#e6edf3' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(200,200,255,0.4)' }
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14].map((days) => (
                        <MenuItem key={days} value={days}>
                          {days} {days === 1 ? 'day' : 'days'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
              
              {/* Different Place Button */}
              {activePointerIndex > 0 ? (
                <Tooltip title="Get alternative destination suggestions">
                  <button
                    type="button"
                    onClick={handleChangeDestination}
                    disabled={isProcessing}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-transition-all tw-duration-150 tw-text-white disabled:tw-opacity-40"
                    style={{ backgroundColor: 'rgb(255,128,0)', border: '1px solid rgba(255,180,80,0.5)' }}
                  >
                    <AutorenewIcon sx={{ fontSize: 14 }} />
                    Change
                  </button>
                </Tooltip>
              ) : (
                <Tooltip title="First destination is fixed by your arrival airport">
                  <span aria-label="First destination is fixed by your arrival airport">
                    <button
                      type="button"
                      disabled
                      className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-border tw-border-surface-border tw-text-xs tw-text-muted tw-opacity-40 tw-cursor-not-allowed"
                    >
                      <AutorenewIcon sx={{ fontSize: 14 }} />
                      Change
                    </button>
                  </span>
                </Tooltip>
              )}
              
              {/* Plan Rest Button - AI Icon */}
              {activePointerIndex < blocks.length - 1 && (
                <Tooltip title="Let AI automatically plan remaining cities">
                  <button
                    type="button"
                    onClick={handleAutoComplete}
                    disabled={isProcessing}
                    aria-label="Let AI automatically plan remaining cities"
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-bg-accent tw-text-white tw-text-xs tw-font-semibold tw-transition-all tw-duration-150 tw-hover:tw-brightness-110 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <CircularProgress size={14} sx={{ color: 'white' }} />
                    ) : (
                      <GeminiIcon style={{ width: 14, height: 14, color: 'white' }} />
                    )}
                    AI Plan
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Itinerary Summary - Only confirmed + current blocks */}
            <div className="tw-mt-3 tw-p-3 tw-rounded-xl tw-bg-surface-3/50 tw-border tw-border-surface-border">
              <span className="tw-text-xs tw-font-semibold tw-text-accent-glow tw-block tw-mb-2">📋 Progress</span>
              <div className="tw-flex tw-flex-col tw-gap-1">
                {blocks.filter((_, idx) => idx <= activePointerIndex).map((block, idx) => {
                  const isConfirmed = idx < activePointerIndex;
                  const isCurrent = idx === activePointerIndex;
                  const isDragging = draggedIndex === idx;
                  const isDragOver = dragOverIndex === idx;
                  const canDrag = idx > 0 && isConfirmed;
                  const canEdit = idx > 0 && isConfirmed;
                  return (
                    <div
                      key={idx}
                      draggable={canDrag}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, idx)}
                      onDragEnd={handleDragEnd}
                      onClick={() => canEdit && handleEditConfirmedSegment(idx)}
                      className={`tw-flex tw-justify-between tw-items-center tw-px-2 tw-py-1.5 tw-rounded-lg tw-border tw-text-xs tw-transition-all tw-duration-150 ${isDragOver ? 'tw-border-dashed tw-border-accent tw-bg-accent/10' : isConfirmed ? 'tw-border-green-500/30 tw-bg-green-500/10' : isCurrent ? 'tw-border-yellow-500/30 tw-bg-yellow-500/10' : 'tw-border-surface-border'} ${canDrag ? 'tw-cursor-grab' : ''} ${isDragging ? 'tw-opacity-50' : ''}`}
                    >
                      <div className="tw-flex tw-items-center tw-gap-1">
                        {canDrag && <DragIndicatorIcon sx={{ fontSize: 14, color: '#8b949e' }} />}
                        <span className={isConfirmed ? 'tw-text-green-400' : isCurrent ? 'tw-text-yellow-400' : 'tw-text-muted'}>
                          {isConfirmed && '✓ '}{isCurrent && '→ '}
                          Day {block.start_day}{block.start_day !== block.end_day ? `–${block.end_day}` : ''}: {block.city}
                        </span>
                      </div>
                      <span className="tw-text-muted">{block.duration}d</span>
                    </div>
                  );
                })}
              </div>
              <span className="tw-text-xs tw-text-muted tw-block tw-mt-2 tw-italic">{activePointerIndex + 1} of {blocks.length} destinations confirmed</span>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Chat Overlay */}
      {showChat && !isComplete && currentBlock && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: '#0d1117',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Chat Header */}
          <Box sx={{ 
            p: 2, 
            bgcolor: '#161b22', 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(99,102,241,0.3)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GeminiIcon style={{ width: 28, height: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>Trip AI Assistant</Typography>
            </Box>
            <IconButton onClick={() => setShowChat(false)} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chat Messages */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 2, 
            bgcolor: '#0d1117',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            {/* Welcome Message */}
            {chat.length === 0 && (
              <Box sx={{ p: 2, bgcolor: 'rgba(99,102,241,0.08)', borderRadius: 2, border: '1px solid rgba(99,102,241,0.25)', maxWidth: 500 }}>
                <Typography variant="body2" sx={{ color: '#c9d1d9' }}>
                  👋 Need help? You can ask me to change destinations, adjust durations, or replan your trip.
                </Typography>
              </Box>
            )}

            {/* Chat History */}
            {chat.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.sender === 'user' ? 'rgba(16,185,129,0.2)' : '#21262d',
                  color: '#c9d1d9',
                  border: msg.sender === 'user' ? '1px solid rgba(16,185,129,0.35)' : '1px solid rgba(99,102,241,0.15)',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{msg.text}</Typography>
                {msg.options && msg.options.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {msg.options.map((opt, oi) => (
                      <Button
                        key={oi}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (opt.autoSubmit) {
                            handleSendMessage(opt.fill ?? '');
                          } else {
                            setUserInput(opt.fill ?? '');
                          }
                        }}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          py: 0.3,
                          px: 1,
                          borderRadius: 2,
                          borderColor: 'rgba(99,102,241,0.4)',
                          color: '#c9d1d9',
                          bgcolor: '#2d333b',
                          '&:hover': { bgcolor: 'rgba(99,102,241,0.15)', borderColor: '#6366f1' }
                        }}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>

          {/* Thinking Steps — visible while isProcessing */}
          {isProcessing && thinkingSteps.length > 0 && (
            <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(99,102,241,0.06)', borderTop: '1px solid rgba(99,102,241,0.15)' }}>
              {thinkingSteps.slice(-1).map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={12} sx={{ color: '#6366f1', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: '#8b949e', fontStyle: 'italic' }}>
                    {step.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Chat Input */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(99,102,241,0.2)', bgcolor: '#161b22', display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isProcessing}
              size="medium"
              sx={{ 
                '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#21262d', color: '#c9d1d9' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.3)' }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={isProcessing || !userInput.trim()}
              sx={{ p: 1.5, bgcolor: '#6366f1', color: 'white', '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: '#21262d', color: '#6e7681' } }}
            >
              {isProcessing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Floating AI Chat Button - Only visible when chat is closed */}
      {!showChat && !isComplete && currentBlock && (
        <Tooltip title="Chat with AI assistant">
          <IconButton
            onClick={() => setShowChat(true)}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 56,
              height: 56,
              bgcolor: '#6366f1',
              color: 'white',
              boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
              zIndex: 1200,
              '&:hover': { bgcolor: '#4f46e5' }
            }}
          >
            <GeminiIcon style={{ width: 28, height: 28 }} />
          </IconButton>
        </Tooltip>
      )}
      </div>
      )}

      {/* Destination Selection Dialog - Better Styled with Unique Cities Only */}
      <Dialog 
        open={showDestinationMenu} 
        onClose={() => setShowDestinationMenu(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid rgba(99,102,241,0.2)' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#e6edf3' }}>
          🔄 Suggest Different Places
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2, borderColor: 'rgba(99,102,241,0.2)' }}>
          {/* Country Filter Toggle Buttons */}
          {(() => {
            const uniqueCountries = [...new Set(availableAlternatives.map(alt => alt.country))].filter(Boolean);
            if (uniqueCountries.length > 1) {
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mb: 1 }}>
                    Filter by country:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      size="small"
                      variant={selectedCountryFilter === 'all' ? 'contained' : 'outlined'}
                      onClick={() => setSelectedCountryFilter('all')}
                      sx={{ 
                        borderRadius: 4,
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 2,
                        fontSize: '0.8rem',
                        bgcolor: selectedCountryFilter === 'all' ? '#6366f1' : '#21262d',
                        color: selectedCountryFilter === 'all' ? 'white' : '#8b949e',
                        borderColor: selectedCountryFilter === 'all' ? '#6366f1' : 'rgba(99,102,241,0.3)',
                        '&:hover': { bgcolor: selectedCountryFilter === 'all' ? '#4f46e5' : '#2d333b', borderColor: '#6366f1' }
                      }}
                    >
                      All ({availableAlternatives.length})
                    </Button>
                    {uniqueCountries.map(country => {
                      const count = availableAlternatives.filter(alt => alt.country === country).length;
                      const isSelected = selectedCountryFilter === country;
                      return (
                        <Button
                          key={country}
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => setSelectedCountryFilter(country)}
                          sx={{ 
                            borderRadius: 4,
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 2,
                            fontSize: '0.8rem',
                            bgcolor: isSelected ? '#6366f1' : '#21262d',
                            color: isSelected ? 'white' : '#8b949e',
                            borderColor: isSelected ? '#6366f1' : 'rgba(99,102,241,0.3)',
                            '&:hover': { bgcolor: isSelected ? '#4f46e5' : '#2d333b', borderColor: '#6366f1' }
                          }}
                        >
                          {country} ({count})
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              );
            }
            return null;
          })()}
          
          <Typography variant="body2" sx={{ mb: 2, color: '#8b949e' }}>
            {availableAlternatives.length > 0 
              ? `Choose from ${selectedCountryFilter === 'all' 
                  ? availableAlternatives.length 
                  : availableAlternatives.filter(a => a.country === selectedCountryFilter).length
                } alternative destination${availableAlternatives.length !== 1 ? 's' : ''}:`
              : 'No alternatives available. Please try again.'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {availableAlternatives.length > 0 ? (
              availableAlternatives
                .filter(alt => selectedCountryFilter === 'all' || alt.country === selectedCountryFilter)
                .map((alt, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  onClick={() => handleSelectAlternative(alt)}
                  disabled={isProcessing}
                  sx={{
                    p: 1.5,
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    bgcolor: '#21262d',
                    borderColor: 'rgba(99,102,241,0.3)',
                    color: '#c9d1d9',
                    '&:hover': {
                      bgcolor: '#2d333b',
                      borderColor: '#6366f1',
                      boxShadow: '0 2px 8px rgba(99,102,241,0.2)'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e6edf3' }}>
                    {alt.city}, {alt.country}
                    {alt.iata && <Typography variant="caption" sx={{ ml: 1, color: '#6e7681' }}>({alt.iata})</Typography>}
                  </Typography>
                  {alt.explanation && (
                    <Typography variant="caption" sx={{ mt: 0.5, color: '#8b949e', lineHeight: 1.4 }}>
                      {alt.explanation}
                    </Typography>
                  )}
                </Button>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#6e7681', py: 2, textAlign: 'center' }}>
                No alternatives found. Please try again.
              </Typography>
            )}
            
            {/* Suggest More Button */}
            <Button
              variant="text"
              onClick={() => handleSuggestMore(selectedCountryFilter)}
              disabled={isProcessing}
              sx={{
                mt: 1,
                color: '#818cf8',
                textTransform: 'none',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'rgba(99,102,241,0.1)'
                }
              }}
            >
              {isProcessing ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading more...
                </>
              ) : (
                <>
                  🔍 Suggest More Places{selectedCountryFilter !== 'all' ? ` in ${selectedCountryFilter}` : ''}
                </>
              )}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(99,102,241,0.2)' }}>
          <Button onClick={() => setShowDestinationMenu(false)} sx={{ color: '#8b949e' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* New Cities Suggestions Dialog */}
      <Dialog 
        open={showNewCitiesDialog} 
        onClose={() => setShowNewCitiesDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid rgba(99,102,241,0.2)' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#e6edf3' }}>
          ✨ Cities to Consider
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2, borderColor: 'rgba(99,102,241,0.2)' }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#8b949e' }}>
            Choose what to do with each city:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {suggestionsList.map((suggestion, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: '#21262d'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#e6edf3' }}>
                  {suggestion.city}, {suggestion.country}
                </Typography>
                {suggestion.explanation && (
                  <Typography variant="caption" sx={{ color: '#8b949e', lineHeight: 1.4, display: 'block', mb: 1 }}>
                    {suggestion.explanation}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={isProcessing}
                    onClick={() => {
                      // Replace current block
                      const updatedBlocks = blocks.map((b, i) =>
                        i === activePointerIndex
                          ? { ...b, city: suggestion.city, country: suggestion.country }
                          : b
                      );
                      const recalculated = recalculateDays(updatedBlocks);
                      setBlocks(recalculated);
                      saveHistory(recalculated);
                      addChatMessage('agent', `✅ Replaced ${blocks[activePointerIndex]?.city} with ${suggestion.city}, ${suggestion.country}.`);
                      setShowNewCitiesDialog(false);
                    }}
                  >
                    ✏️ Replace {blocks[activePointerIndex]?.city || 'current'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={isProcessing}
                    onClick={() => {
                      // Insert after current block
                      const insertAt = activePointerIndex + 1;
                      const newBlock = {
                        city: suggestion.city,
                        country: suggestion.country,
                        duration: 3,
                        start_day: 0,
                        end_day: 0,
                        status: 'Pending',
                        order_index: insertAt,
                        explanation: suggestion.explanation || '',
                        iata: suggestion.iata || ''
                      };
                      const updatedBlocks = [
                        ...blocks.slice(0, insertAt),
                        newBlock,
                        ...blocks.slice(insertAt)
                      ];
                      const recalculated = recalculateDays(updatedBlocks);
                      setBlocks(recalculated);
                      saveHistory(recalculated);
                      addChatMessage('agent', `✅ Added ${suggestion.city}, ${suggestion.country} after ${blocks[activePointerIndex]?.city || 'current stop'} for 3 days.`);
                      setShowNewCitiesDialog(false);
                    }}
                  >
                    ➕ Add to trip
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(99,102,241,0.2)' }}>
          <Button onClick={() => setShowNewCitiesDialog(false)} sx={{ color: '#8b949e' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InteractiveRouteBuilder;
